import type { Matcher } from 'react-day-picker';

import { TooltipProps as TooltipPropsM } from '@mui/material/Tooltip';

import type { SelectButtonType } from '../../utils/getDateRangeBySelectButton.js';
import DatePickerRangeV3 from '../DatePickerRangeV3/DatePickerRange.jsx';

export interface DatepickerRangeProps {
  /**
   * If true calendar is open
   * @deprecated use isOpen instead with new DatePicker
   */
  open?: boolean;
  /**
   * If true calendar is open
   */
  isOpen?: boolean;
  /**
   * Callback for calendar opening
   * @deprecated
   */
  onCalendarOpen?: () => void;
  /**
   * Callback for calendar closing
   * @deprecated use onApply instead
   */
  onCalendarClose?: () => void;
  /**
   * The first day of the week
   * @deprecated
   */
  calendarStartDay?: number /**
   * if true calendar should be close on select the date
   * @deprecated
   */;
  shouldCloseOnSelect?: boolean;
  /**
   * Format of displaying date
   * dd/MM/yyyy by default
   * @deprecated not using in favour of standart locale formating
   */
  dateFormat?: string | string[];
  /**
   * The selected date
   * @deprecated use startDate and endDate instead
   */
  selected?: Date | null;
  /**
   * Callback when state is changing
   * @deprecated use onApply instead
   */
  onChange: (
    date: [Date | null, Date | null],
    e: React.SyntheticEvent<any, Event> | undefined,
  ) => void;
  /**
   * @deprecated use onApply instead
   */
  onClickOutside?: () => void;
  /**
   * The range start date
   */
  startDate?: Date | null;
  /**
   * The range end date
   */
  endDate?: Date | null;
  /**
   * Additional class name for the component
   */
  className?: string;
  /**
   * Array of dates to mark events
   */
  eventDates?: Date[];
  /**
   * Color of the input
   */
  color?: 'primary' | 'secondary';
  /**
   * Label of the input
   */
  label?: string;
  /**
   * Props fot input element to make it fullWidth
   */
  fullWidth?: boolean;
  /**
   * The helperText of the input. It is only used for layout. The actual labelling is handled by HelperText.
   */
  helperText?: string;
  /**
   * If true start date will be disabled and can't change
   */
  disableStartDate?: boolean;
  /**
   * Handler that fires when the datepickerRange closes
   */
  onApply?: (
    dateRange?: { from?: Date | null; to?: Date | null },
    selectedButton?: SelectButtonType,
  ) => void;
  /**
   * If true, the datepicker will be in error status
   */
  error?: boolean;
  /**
   * The size can be small or medium
   * @default medium
   */
  size?: 'small' | 'medium';
  /**
   * The info text of the input
   */
  infoText?: string;
  /**
   * If true, the datepicker will be disabled
   */
  disabled?: boolean;
  /**
   * If true, the datepicker will be in warning status
   */
  warning?: boolean;
  /**
   * If true, the datepicker will be in readOnly status
   */
  readOnly?: boolean;
  /**
   * Props for the info text tooltip
   */
  InfoTextTooltipProps?: Partial<TooltipPropsM>;
  /**
   * @deprecated already built in
   */
  endAdornment?: React.ReactNode;
  /**
   * @deprecated we don't use popper anymore
   */
  popperContainer?(props: { children: React.ReactNode[] }): React.ReactNode;
  /*
   * If true, the datepicker will be in sidebar mode with buttons
   */
  sidebar?: boolean;
  /**
   * The placement of the datepicker
   * @default bottom-start
   */
  placement?:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';
  /**
   * @deprecated use disabledDates instead
   * disabledDates supports Matcher
   */
  minDate?: Date | null;
  /**
   * @deprecated use disabledDates instead
   * disabledDates supports Matcher
   */
  maxDate?: Date | null;
  /**
   * @deprecated use disabledDates instead
   * disabledDates supports Matcher
   */
  filterDate?: (date: Date) => boolean;
  /**
   * Helps to disable dates in various ways
   * Look at https://daypicker.dev/api/type-aliases/Matcher
   */
  disabledDates?: Matcher;

  /**
   * onFocus event handler for the input
   *   * @description Use `onApply` instead
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * onBlur event handler for the input
   *  * @description Use `onCancel` instead
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

  /**
   * If true, the datepicker input will be required
   */
  required?: boolean;
  /**
   * If true, the portal will be disabled. Props for the popper component that helps to control the position of the calendar (also works with Modal Dialog)
   */
  disablePortal?: boolean;
  /**
   * Handler that fires when the datepickerRange closes
   */
  onCancel?: () => void;
  /**
   * Props for the popper component
   * @deprecated
   */
  popperProps?: { strategy: 'fixed' | 'absolute' };
  /**
   * Prop that turn off 'close' icon button in input.
   * @default true
   */
  clearable?: boolean;
  /**
   * Callback that adds extra logic to `close` icon button in input.
   */
  onClear?: () => void;
  /**
   * The selected sidebar button, if it was saved beforehand
   */
  selectedButton?: SelectButtonType;
  /**
   * All time button configuration. For using need to pass from and to dates
   */
  allTimeButton?: {
    from?: Date;
    to?: Date;
  };
}

/**
 * DatepickerRange component
 * This wrapper is temporary and will be removed after the migration to the new datepicker is completed
 * the criteria of removal is ensure that all deprecated props are removed
 */
export default function DatePickerRange({
  placement = 'bottom-start',
  ...props
}: DatepickerRangeProps) {
  const disabledDatesMinMax =
    props.minDate && props.maxDate
      ? {
          after: props.maxDate,
          before: props.minDate,
        }
      : undefined;

  const disabledMax = props.maxDate ? { after: props.maxDate } : undefined;
  const disabledMin = props.minDate ? { before: props.minDate } : undefined;
  const disableViaFilterDate = props.filterDate
    ? (date: Date) => !props.filterDate?.(date)
    : undefined;

  return (
    <DatePickerRangeV3
      {...props}
      selected={{ from: props.startDate, to: props.endDate }}
      sidebar={props.sidebar ?? false}
      isOpen={props.isOpen || props.open}
      className={props.className}
      eventDates={props.eventDates}
      color={props.color}
      label={props.label}
      fullWidth={props.fullWidth}
      helperText={props.helperText}
      error={props.error}
      infoText={props.infoText}
      disabled={props.disabled}
      // the order is important
      disabledDates={
        props.disabledDates ||
        // // it is important to backward compatibility with old datepicker
        disableViaFilterDate ||
        disabledDatesMinMax ||
        disabledMax ||
        disabledMin
      }
      InfoTextTooltipProps={props.InfoTextTooltipProps}
      warning={props.warning}
      readOnly={props.readOnly}
      disablePortal={props.disablePortal}
      size={props.size}
      placement={placement}
      onCancel={props.onCancel}
      onApply={(date, selectedButton) => {
        props.onChange([date?.from ?? null, date?.to ?? null], undefined);
        props.onCalendarClose?.();
        props.onClickOutside?.();
        props?.onApply?.(date, selectedButton);
      }}
      clearable={props.clearable}
      onClear={props.onClear}
    />
  );
}
